import { useState, useCallback, useRef, useEffect } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import type { ChatStatus } from "ai";
import { mergeMessages, isCanonical } from "@/lib/message-store";
import {
  StreamMarkerParser,
  type StreamMarkerOp,
} from "@/lib/stream-parser";
import i18n, { getCurrentLocale } from "@/lib/i18n";
import { mergeLiveToolDetails } from "@/lib/tool-details";
import { mapSessionMessage } from "@/lib/session-message-mapper";
import {
  beginVoiceNoteProgress,
  type VoiceNoteProgressHandle,
} from "@/lib/voice-note-progress";

function generateId() {
  return Math.random().toString(36).slice(2, 11);
}

interface InFlightLiveTool {
  id: string;
  name: string;
}

/**
 * Associate terminal stream markers only with a locally observed start in
 * this stream. A tool name is not a durable identity, but it is enough to
 * avoid assigning an out-of-order terminal event for one tool to a different
 * local tool. Repeated same-name calls retain their stream-local FIFO order.
 */
function takeInFlightLiveTool(
  tools: InFlightLiveTool[],
  toolName: string,
): string | undefined {
  const index = tools.findIndex((tool) => tool.name === toolName);
  if (index === -1) return undefined;
  return tools.splice(index, 1)[0]?.id;
}

// Voice-note transcription is a convenience step before the real chat run.
// Never let a slow/retrying STT request block message submission indefinitely:
// if it times out, the already-uploaded audio remains attached and the run
// proceeds without a precomputed transcript.
const VOICE_TRANSCRIPTION_TIMEOUT_MS = 45_000;

// Attachment upload is also best-effort for chat submission: if the multipart
// proxy/backend stalls, proceed with local attachment chips instead of leaving
// the composer stuck forever. Scale with payload size so large legitimate
// uploads are not cut off by the small-file timeout.
const UPLOAD_TIMEOUT_BASE_MS = 60_000;
const UPLOAD_TIMEOUT_PER_MIB_MS = 1_000;
const UPLOAD_TIMEOUT_MAX_MS = 30 * 60_000;
const FAILED_RUN_CONTINUATION_PROMPT =
  "Continue the previous request from the saved conversation. Do not repeat work already completed.";
const TERMINAL_RUN_STATUSES = new Set([
  "completed",
  "failed",
  "cancelled",
  "timed_out",
]);

export interface RecoverableFailedRun {
  runId: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyRunId(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * The runs endpoint is intentionally parsed as untrusted data: servers that
 * predate recovered-failed status support simply produce no UI signal.
 */
function latestRunFromResponse(data: unknown): Record<string, unknown> | null {
  if (!isRecord(data) || !Array.isArray(data.runs)) return null;
  const latestRun = data.runs[0];
  return isRecord(latestRun) ? latestRun : null;
}

function recoverableFailedRunFromResponse(
  data: unknown,
): RecoverableFailedRun | null {
  const latestRun = latestRunFromResponse(data);
  if (
    !latestRun
    || latestRun.status !== "failed"
    || !isNonEmptyRunId(latestRun.run_id)
  ) {
    return null;
  }
  return { runId: latestRun.run_id };
}

function runsFromResponse(data: unknown): Record<string, unknown>[] {
  if (!isRecord(data) || !Array.isArray(data.runs)) return [];
  return data.runs.filter(isRecord);
}

// ─── File upload helper ─────────────────────────────────────────────────────

function uploadTimeoutMs(files: any[]): number {
  const totalBytes = files.reduce((sum, f) => {
    const rawFile = (f as any).rawFile;
    const size = rawFile instanceof File ? rawFile.size : Number((f as any).size ?? 0);
    return Number.isFinite(size) && size > 0 ? sum + size : sum;
  }, 0);
  const totalMiB = totalBytes / (1024 * 1024);
  return Math.min(
    UPLOAD_TIMEOUT_MAX_MS,
    UPLOAD_TIMEOUT_BASE_MS + Math.ceil(totalMiB) * UPLOAD_TIMEOUT_PER_MIB_MS,
  );
}

/**
 * Shape returned by POST /api/sessions/{sid}/attachments
 * (mirrors the agno-server `UploadResponse` schema).
 */
export interface UploadedAttachment {
  path: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
}
interface UploadResponse {
  attachments: UploadedAttachment[];
}

/**
 * Upload files to the agent workspace via the chat-bridge multipart proxy
 * BEFORE sending the chat message.
 *
 * Files are POSTed to `/api/sessions/{sid}/attachments?agent_id={agentId}`
 * as `multipart/form-data`. The proxy streams the raw body through to the
 * agno-server without buffering it in memory, making 2 GB uploads feasible.
 *
 * Each file in the `files` array is a `FileUIPart` with a `url` field that
 * is either a `blob:` URL (from file selection) or a `data:` URL (from
 * screenshot capture / voice recording).  When the file was selected from
 * disk, a `rawFile` property holds the original `File` object — we use it
 * directly to avoid `fetch(blob:...)`, which is blocked by
 * Content-Security-Policy on some hosts.  For `data:` URLs (no `File`
 * object available), we fall back to fetching the URL.
 *
 * @returns Array of absolute workspace paths for the saved files.
 * @throws if the upload fails (network error, server 4xx/5xx).
 */
export async function uploadAttachmentRefsToServer(
  sessionId: string,
  agentId: string | null | undefined,
  files: any[],
  fetchWithAuth: (url: string, init: RequestInit) => Promise<Response>,
  signal?: AbortSignal,
): Promise<UploadedAttachment[]> {
  if (!agentId) {
    throw new Error(i18n.t("input.errors.noAgentForUpload"));
  }

  const formData = new FormData();
  for (const f of files) {
    const filename = f.filename ?? f.name ?? "upload";

    // Prefer the raw File object when available — this avoids fetch(blob:...)
    // which is blocked by Content-Security-Policy on some hosts (e.g.
    // catchall).  The rawFile is set by PromptInput when the user selects
    // files from disk.
    //
    // Fall back to fetch(url) for data: URLs (screenshots, voice recordings)
    // that don't have a File object — data: URLs are not blocked by CSP.
    if ((f as any).rawFile instanceof File) {
      formData.append("files", (f as any).rawFile, filename);
      continue;
    }

    const url = f.url as string;
    if (!url) {
      console.warn("[useAgnoChat] uploadFilesToServer: file has no url and no rawFile, skipping", f);
      continue;
    }
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(i18n.t("input.errors.fileFetchFailed", {
        status: res.status,
        statusText: res.statusText,
      }));
    }
    const blob = await res.blob();
    formData.append("files", blob, filename);
  }

  if (formData.get("files") === null) {
    // No files could be converted — nothing to upload
    return [];
  }

  const uploadUrl = `/api/sessions/${sessionId}/attachments?agent_id=${encodeURIComponent(agentId)}`;
  console.info(`[useAgnoChat] uploadFilesToServer: POST ${uploadUrl} | ${files.length} file(s)`);

  const uploadController = new AbortController();
  let didUploadTimeout = false;
  const timeoutMs = uploadTimeoutMs(files);
  const timeoutId = window.setTimeout(() => {
    didUploadTimeout = true;
    uploadController.abort();
  }, timeoutMs);
  const abortUpload = () => uploadController.abort();
  signal?.addEventListener("abort", abortUpload, { once: true });

  let response: Response;
  try {
    response = await fetchWithAuth(uploadUrl, {
      method: "POST",
      body: formData,
      signal: uploadController.signal,
    });
  } catch (err) {
    if (didUploadTimeout) {
      throw new Error(i18n.t("input.errors.uploadTimedOut", {
        seconds: Math.round(timeoutMs / 1000),
      }));
    }
    throw err;
  } finally {
    window.clearTimeout(timeoutId);
    signal?.removeEventListener("abort", abortUpload);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(i18n.t("input.errors.uploadFailed", {
      status: response.status,
      statusText: response.statusText,
      details: text.substring(0, 200),
    }));
  }

  const data = (await response.json()) as UploadResponse;
  return data.attachments;
}

async function uploadFilesToServer(
  sessionId: string,
  agentId: string | null | undefined,
  files: any[],
  fetchWithAuth: (url: string, init: RequestInit) => Promise<Response>,
  signal?: AbortSignal,
): Promise<string[]> {
  const uploaded = await uploadAttachmentRefsToServer(sessionId, agentId, files, fetchWithAuth, signal);
  return uploaded.map((attachment) => attachment.path);
}

export interface AgnoChatMessage {
  id: string;
  role: "user" | "assistant" | "system" | "tool";
  parts: Array<AgnoChatMessagePart>;
  content?: string;
  metadata?: Record<string, any>;
  /**
   * Client-only identity for a user boundary whose canonical DB row has a
   * different id. Preserves tool-group disclosure through reconciliation.
   */
  toolGroupBoundaryId?: string;
  /** Set when the assistant message was interrupted by a network error */
  streamingError?: boolean;
  /** When the user resumes, the new text replaces the old content */
  resumeTimestamp?: number;
}

export type AgnoChatMessagePart =
  | { type: "text"; text: string }
  | { type: "file"; [key: string]: any };

/**
 * Lets the feed serialize a recovery-poll tail merge with an older-history
 * prepend. The callback resolves only once the feed has committed the merge,
 * so Virtua never receives both mutations in the same render transaction.
 */
export type QueueRecoveryMessageMerge = (commit: () => void) => Promise<void>;

export interface UseAgnoChatOptions {
  bootstrapRuns?: import("@/api/conversation-bootstrap").BootstrapRun[];
  bootstrapPending?: boolean;
  sessionId: string;
  agentId?: string | null;
  fetchWithAuth: (url: string, init: RequestInit) => Promise<Response>;
  /**
   * When true, the mount-time /runs poll-and-recover loop is skipped.
   * Use ONLY for sessions this very component just created via POST —
   * the server cannot have an in-flight run for a session it returned
   * milliseconds ago, so the check would always return zero anyway.  The
   * caller is responsible for clearing this flag after the first render so
   * a later revisit of the same session id falls back to the normal check.
   */
  isNewSession?: boolean;
  /** Called once for each structured terminal tool result in live streams. */
  onToolCompleted?: (event: { toolName: string; result: unknown }) => void;
  /**
   * Optional UI-owned commit queue for canonical rows read by recovery polling.
   * Live SSE updates intentionally do not use this: their high-frequency tail
   * mutations retain the existing, conservative history-pagination guard.
   */
  queueRecoveryMessageMerge?: QueueRecoveryMessageMerge;
  /** Keep canonical recovery rows in the live tail while an old window is visible. */
  historicalWindowRef?: { current: boolean };
  onHistoricalTailMessages?: (messages: AgnoChatMessage[]) => void;
  /** Sending from old history first restores the live surface. */
  beforeSendFromHistoricalWindow?: () => void | Promise<void>;
}

export interface UseAgnoChatReturn {
  messages: AgnoChatMessage[];
  status: ChatStatus;
  /** True only while canonical updates are being driven by recovery polling. */
  isPolling: boolean;
  sendMessage: (message: { text: string; files?: any[] }) => Promise<SendMessageResult>;
  /** The latest run can be continued by starting a fresh normal turn. */
  recoverableFailedRun: RecoverableFailedRun | null;
  /**
   * Re-validates the recovered failed run, then starts a fresh normal turn.
   * This is deliberately separate from the HITL-only continueRun route.
   */
  continueFromFailedRun: () => Promise<void>;
  stop: () => void;
  setMessages: React.Dispatch<React.SetStateAction<AgnoChatMessage[]>>;
  /**
   * True while files are being uploaded to the server (before the chat
   * message is sent).  Exposed separately from ``status`` because the AI
   * SDK's ``ChatStatus`` type has no "uploading" value — the previous code
   * hacked around this with ``setStatus("uploaded" as ChatStatus)``, which
   * fell through to the default submit icon with no spinner.  The UI should
   * use this flag to show a spinner on the submit button and an
   * "Uploading…" indicator while the multipart upload is in flight.
   */
  isUploading: boolean;
  /**
   * True while a voice-note audio file is being transcribed via the
   * ``/api/transcribe`` proxy (ElevenLabs Scribe v2), AFTER files have been
   * uploaded but BEFORE the optimistic user message is inserted and the chat
   * message is sent.  Like ``isUploading``, this is a separate flag because
   * ``ChatStatus`` has no "transcribing" value — ``status`` is still
   * ``"ready"`` during this phase.  The UI should show a spinner on the
   * submit button and a "Transcribing…" indicator so the user sees
   * continuous loading feedback from upload → transcribe → stream instead
   * of a dead zone where nothing appears to be happening.
   */
  isTranscribing: boolean;
  /**
   * Resume a previously-interrupted stream via polling.
   * Safe to call whenever status === "error" and the last assistant message
   * has streamingError set. No lastEventId required.
   */
  resume: () => Promise<void>;
  /**
   * Continue a paused HITL run. Called when the user approves or rejects
   * a tool that required human confirmation. POSTs to the /continue
   * endpoint and streams the resumed response.
   */
  continueRun: (
    runId: string,
    tools: string,
    secretInputs?: { requirement_id: string; value: string },
    onAccepted?: () => void,
  ) => Promise<void>;
  /** True when the last stream paused for HITL approval. The UI should
   *  skip post-stream reconciliation (dropOptimistic) while this is set,
   *  otherwise the client-side approval card is wiped. */
  pausedForHitl: React.MutableRefObject<boolean>;
}

/** Outcome of a user send, used by the composer to retain failed drafts. */
export interface SendMessageResult {
  /** `sent` means the chat request was accepted and a stream event arrived. */
  outcome: "sent" | "failed" | "cancelled";
  error?: unknown;
}

export function useAgnoChat({
  bootstrapRuns,
  bootstrapPending = false,
  sessionId,
  agentId,
  fetchWithAuth,
  isNewSession,
  onToolCompleted,
  queueRecoveryMessageMerge,
  historicalWindowRef,
  onHistoricalTailMessages,
  beforeSendFromHistoricalWindow,
}: UseAgnoChatOptions): UseAgnoChatReturn {
  const [messages, setMessages] = useState<AgnoChatMessage[]>([]);
  const [status, setStatus] = useState<ChatStatus>("ready");
  const [isPolling, setIsPolling] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recoverableFailedRun, setRecoverableFailedRun] =
    useState<RecoverableFailedRun | null>(null);

  const messagesRef = useRef(messages);
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  const abortRef = useRef<AbortController | null>(null);
  const lastEventIdRef = useRef<string | null>(null);
  const assistantIdRef = useRef<string | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRunIdRef = useRef<string | null>(null);
  // Exposed so resume() can trigger the same polling path used on session mount.
  const checkAndPollRef = useRef<() => Promise<void>>(async () => {});
  useRefreshOnReconnect(() => {
    if (abortRef.current) return;
    return checkAndPollRef.current();
  });
  /** Tracks the last message id we have already polled, so we can fetch only newer messages. */
  const lastPolledMessageIdRef = useRef<string | null>(null);
  /**
   * Monotonically increasing owner for recovery polling. A recovery fetch can
   * be in flight when the user starts a new live SSE send; clearing the poll
   * timer alone does not stop that fetch from resolving later and writing its
   * stale result into the shared message list. Every recovery write therefore
   * checks the generation it started under.
   */
  const recoveryGenerationRef = useRef(0);
  /** Set to true when the last stream paused for HITL approval. Prevents the
   *  post-stream reconciliation effect in main.tsx from dropping the
   *  client-side approval card (which has no DB timestamp and would be
   *  removed by `dropOptimistic`). Cleared when continueRun() is called. */
  const pausedForHitlRef = useRef(false);
  /** Set to true when resume() proactively aborts a stuck SSE reader (e.g.
   *  when the tab was hidden and the stream broke silently). Prevents the
   *  sendMessage catch block from running its own recovery logic (setting
   *  streamingError, entering the auto-resume loop) in parallel with the
   *  resume() code path, which would race and leave the UI in a broken
   *  state. Cleared at the start of the next sendMessage() call. */
  const abortedForRecoveryRef = useRef(false);
  /** Tracks whether at least one SSE event chunk has arrived from /chat/web.
   *  Used by the unmount cleanup to decide whether it is safe to abort
   *  streamController without orphaning the server-side run. Reset to `false`
   *  at the top of every sendMessage() call and flipped to `true` on the
   *  first non-empty `reader.read()` value in the SSE loop. */
  const hasReceivedFirstEventRef = useRef(false);
  /** Holds the upload-side abort controller for the current sendMessage().
   *  Stored in a ref so stop() can abort an in-flight upload / transcription
   *  even after sendMessage() is awaiting its own loop in a different micro-
   *  task. Distinct from `abortRef` (which holds streamController) so the
   *  unmount cleanup intentionally does NOT abort the upload. */
  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  /**
   * Voice-note preparation handles owned by this hook instance. Unlike the
   * AbortController, these are intentionally left alone on unmount so the
   * module-level store can keep reporting background work after navigation.
   * stop() completes them immediately because it is an explicit cancellation.
   */
  const voiceNoteProgressHandlesRef = useRef<Set<VoiceNoteProgressHandle>>(new Set());
  const onToolCompletedRef = useRef(onToolCompleted);
  onToolCompletedRef.current = onToolCompleted;
  const queueRecoveryMessageMergeRef = useRef(queueRecoveryMessageMerge);
  queueRecoveryMessageMergeRef.current = queueRecoveryMessageMerge;
  const historicalWindowRefRef = useRef(historicalWindowRef);
  historicalWindowRefRef.current = historicalWindowRef;
  const onHistoricalTailMessagesRef = useRef(onHistoricalTailMessages);
  onHistoricalTailMessagesRef.current = onHistoricalTailMessages;
  const beforeSendFromHistoricalWindowRef = useRef(beforeSendFromHistoricalWindow);
  beforeSendFromHistoricalWindowRef.current = beforeSendFromHistoricalWindow;

  const notifyToolCompleted = (op: Extract<StreamMarkerOp, { type: "tool_marker" }>) => {
    const result = op.details?.result;
    if (op.status !== "end" || result?.state !== "available") return;
    try {
      onToolCompletedRef.current?.({ toolName: op.toolName, result: result.value });
    } catch (error) {
      console.warn("[useAgnoChat] onToolCompleted callback failed:", error);
    }
  };

  const commitRecoveryMessageMerge = useCallback(async (commit: () => void) => {
    const queue = queueRecoveryMessageMergeRef.current;
    if (queue) {
      await queue(commit);
      return;
    }
    commit();
  }, []);

  /** Stop recovery polling and invalidate any already in-flight poll result. */
  const cancelRecovery = useCallback(() => {
    recoveryGenerationRef.current += 1;
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    activeRunIdRef.current = null;
    lastPolledMessageIdRef.current = null;
    setIsPolling(false);
  }, []);

  const updateRecoverableFailedRun = useCallback((data: unknown) => {
    const next = recoverableFailedRunFromResponse(data);
    setRecoverableFailedRun((current) => (
      current?.runId === next?.runId ? current : next
    ));
    return next;
  }, []);

  // Keep ref in sync with state so sendMessage always sees the latest messages
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Ref to track status without adding it to effect deps (avoids loops)
  const statusRef = useRef<ChatStatus>(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  // Reset state when the session changes (replaces the key={sessionId} remount hack)
  useEffect(() => {
    cancelRecovery();
    setMessages([]);
    statusRef.current = "ready";
    setStatus("ready");
    setIsUploading(false);
    setRecoverableFailedRun(null);
    lastEventIdRef.current = null;
    assistantIdRef.current = null;
    abortRef.current?.abort();
  }, [sessionId, cancelRecovery]);

  // ── On session mount: detect in-progress runs and poll messages ──
  //
  // Skip the mount-time recovery loop entirely for sessions we just created
  // via POST.  The server cannot have an in-flight run for a session id it
  // minted milliseconds ago, so the /runs check would always return zero,
  // and the follow-up /messages fetch is redundant with the historical-load
  // effect in ChatInterface.  Skipping both saves two of the six redundant
  // GETs observed in the new-session request-multiplier bug.
  //
  // `isNewSession` is intentionally NOT in the dependency array: it is a
  // primitive snapshot of the prop at first mount, and we want to keep the
  // decision sticky once made.  Re-running the effect when the parent later
  // flips the flag back to false would re-issue a /runs fetch for a session
  // we already know is freshly empty — pure waste.
  useEffect(() => {
    let cancelled = false;
    // Single-flight guard for the poll loop within this effect generation.
    // Without it, two concurrent entries into the loop (e.g. the mount-time
    // checkAndPoll plus a resume()-driven checkAndPoll) could each schedule a
    // timeout, orphaning one and doubling the request rate on every tick.
    let pollInFlight = false;
    // If a new recovery generation starts while an older poll is awaiting a
    // response, hand the new generation back to that poll's finally block.
    // Otherwise the new call would return at the single-flight guard and the
    // stale poll (correctly barred from rescheduling itself) would leave the
    // new recovery with no next tick.
    let pendingPollGeneration: number | null = null;

    // Was a polling recovery loop already driving this mount when the effect
    // re-ran?  This effect re-runs whenever `fetchWithAuth` changes identity —
    // which happens when AuthKit settles the user object shortly after load.
    // The cleanup below clears the poll timeout, so without an explicit restart
    // the loop would die mid-run.  `activeRunIdRef` is only set while a polling
    // loop owns the stream (the live-SSE path never sets it), so it is a
    // reliable "was polling" signal.
    const wasPolling = activeRunIdRef.current !== null;

    /**
     * Claim a new recovery generation. Any older fetch may still resolve, but
     * its generation checks below make it a no-op instead of letting it merge
     * stale canonical rows over a live optimistic send.
     */
    const beginRecovery = () => {
      const generation = ++recoveryGenerationRef.current;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      activeRunIdRef.current = null;
      lastPolledMessageIdRef.current = null;
      return generation;
    };
    const ownsRecovery = (generation: number) =>
      !cancelled && recoveryGenerationRef.current === generation;
    const setRecoveryStatus = (generation: number, nextStatus: ChatStatus) => {
      if (!ownsRecovery(generation)) return;
      statusRef.current = nextStatus;
      // A send can begin after this update is queued but before React applies
      // it. Recheck in the updater so an old poll cannot put the UI back into
      // "streaming" or "ready" over the newer send's status.
      setStatus((current) =>
        ownsRecovery(generation) ? nextStatus : current,
      );
    };
    const setRecoveryPolling = (generation: number, nextIsPolling: boolean) => {
      if (!ownsRecovery(generation)) return;
      // Match setRecoveryStatus's ownership check in the updater so a stale
      // recovery tick cannot re-label a newly started SSE stream as polling.
      setIsPolling((current) => (
        ownsRecovery(generation) ? nextIsPolling : current
      ));
    };
    const initialRecoveryGeneration = beginRecovery();

    // Install the poll driver up front so resume() always has a real
    // implementation to call — even for freshly-created sessions that skip the
    // mount-time /runs check below. Each explicit resume claims a fresh
    // generation too, so an older poll cannot regain ownership after a new
    // live SSE send invalidated it.
    checkAndPollRef.current = async () => {
      const generation = beginRecovery();
      await checkAndPoll(generation);
    };

    // Decide whether to (re)start recovery polling:
    //   • Fresh mount (status "ready", no live SSE) → check /runs and recover.
    //   • Effect re-ran mid-recovery while polling owned the stream and no live
    //     SSE is active → restart the loop the cleanup just cancelled.
    // Never poll over an active SSE reader (abortRef set): that path already
    // owns the live update stream and polling would duplicate it.  Also skip
    // the mount-time check for freshly-created sessions (isNewSession) — the
    // server cannot have an in-flight run for a session it minted milliseconds
    // ago — but note checkAndPollRef is still installed above.
    const liveSSE = abortRef.current !== null;
    const shouldRecover =
      !isNewSession &&
      !bootstrapPending &&
      !liveSSE &&
      (statusRef.current === "ready" ||
        (statusRef.current === "streaming" && wasPolling));

    if (shouldRecover) {
      void checkAndPoll(initialRecoveryGeneration, bootstrapRuns);
    }

    async function checkAndPoll(generation: number, initialRuns?: import("@/api/conversation-bootstrap").BootstrapRun[]) {
      if (!ownsRecovery(generation)) return;
      try {
        const res = initialRuns ? null : await fetchWithAuth(
          `/api/sessions/${sessionId}/runs`,
          { headers: {} }
        );
        if (res && !res.ok) return;
        if (!ownsRecovery(generation)) return;
        const data: unknown = initialRuns ? { runs: initialRuns } : await res!.json();
        if (!ownsRecovery(generation)) return;
        // Only the LATEST run determines session status. The backend returns
        // runs in reverse chronological order (newest first), so runs[0] is
        // the most recent.  Iterating with Array#find would mark the session
        // as pending if ANY run in the list is running — including stale older
        // turns from prior conversations that were never properly terminalised
        // — which kept the "streaming" spinner on forever and prevented the
        // user from sending new messages.  Restrict the check to the latest
        // run so we only resume polling when the current turn is actually
        // in-flight.
        const latestRun = latestRunFromResponse(data);
        updateRecoverableFailedRun(data);
        if (!latestRun || !ownsRecovery(generation)) return;
        const latestRunStatus = latestRun.status;
        const latestRunId = latestRun.run_id;

        // HITL: if the latest run is paused for human approval, do a
        // one-shot fetch of the latest messages (which will include the
        // hitl_paused marker row) and set status to "ready" so the
        // approval card is interactive.  Do NOT enter the streaming-poll
        // loop — the run is waiting for the user to approve/reject, not
        // for more content to arrive.
        if (latestRunStatus === "paused") {
          if (!isNonEmptyRunId(latestRunId)) return;
          if (!ownsRecovery(generation)) return;
          activeRunIdRef.current = latestRunId;
          // One-shot fetch — not pollMessages() which guards on
          // status="streaming" and would bail out.
          try {
            const msgRes = await fetchWithAuth(
              `/api/sessions/${sessionId}/messages?limit=50&complete_turns=true`,
              { headers: {} },
            );
            if (msgRes.ok && ownsRecovery(generation)) {
              const msgData = await msgRes.json();
              if (!ownsRecovery(generation)) return;
              const rows = msgData.messages || [];
              if (rows.length > 0) {
                const incoming = rows.map(mapSessionMessage);
                if (historicalWindowRefRef.current?.current) {
                  onHistoricalTailMessagesRef.current?.(incoming);
                } else {
                  await commitRecoveryMessageMerge(() => {
                    setMessages((prev) =>
                      ownsRecovery(generation)
                        ? mergeMessages(prev, incoming)
                        : prev,
                    );
                  });
                }
              }
            }
          } catch (e) {
            if (ownsRecovery(generation)) {
              console.warn("[useAgnoChat] HITL paused message fetch failed:", e);
            }
          }
          setRecoveryPolling(generation, false);
          setRecoveryStatus(generation, "ready");
          return;
        }

        if (TERMINAL_RUN_STATUSES.has(String(latestRunStatus))) {
          // A recovery check can race the terminal server transition after an
          // SSE disconnect or while the polling effect is being restarted.
          // Do not leave the local transport status stuck in "streaming": the
          // feed's existing streaming -> ready reconciliation owns any final
          // canonical tail rows that landed after this check.
          setRecoveryPolling(generation, false);
          if (
            statusRef.current === "submitted"
            || statusRef.current === "streaming"
          ) {
            setRecoveryStatus(generation, "ready");
          }
          return;
        }

        // Keep the old-server/malformed-response behavior for unfamiliar
        // statuses. Only confirmed terminal values may end local recovery.
        if (latestRunStatus !== "running") return;
        if (!isNonEmptyRunId(latestRunId)) return;
        // Update the ref synchronously as well as the state. `statusRef` is
        // normally kept in sync via a useEffect that only runs *after* render,
        // so right after setStatus("streaming") the ref still reads "ready".
        // pollMessages() (called immediately below) guards on
        // `statusRef.current !== "streaming"` and would otherwise bail out on
        // its very first run — silently killing the entire polling loop.
        if (!ownsRecovery(generation)) return;
        activeRunIdRef.current = latestRunId;
        setRecoveryPolling(generation, true);
        setRecoveryStatus(generation, "streaming");

        // Cancel any previously scheduled tick so a second entry into
        // checkAndPoll (e.g. via resume()) doesn't run two loops in parallel.
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
        // Immediate first poll so the UI shows something right away
        await pollMessages(generation);
      } catch (err) {
        if (ownsRecovery(generation)) {
          console.error("[useAgnoChat] Failed to check runs:", err);
        }
      }
    }

    async function pollMessages(generation: number) {
      // Single-flight: never let two poll bodies run concurrently (see the
      // pollInFlight declaration at the top of the effect).  A second caller
      // hands its generation back to the in-flight body's finally block.
      if (!ownsRecovery(generation)) return;
      if (pollInFlight) {
        pendingPollGeneration = generation;
        return;
      }
      if (!activeRunIdRef.current) return;
      // If user started a new message (status changed from streaming),
      // stop polling and let the SSE path take over.
      if (statusRef.current !== "streaming") {
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
        return;
      }

      pollInFlight = true;
      try {
        const activeRunId = activeRunIdRef.current;
        const ownsActiveRun = () =>
          ownsRecovery(generation) && activeRunIdRef.current === activeRunId;

        // ── Fetch messages FIRST ──────────────────────────────────────────
        // Previously every tick fetched `/runs` *and* `/messages` (two
        // requests/second).  The run's assistant/tool rows carry a per-row
        // `status` (`in_progress` while being written, then `complete`/
        // `error`), so we can usually tell the run is still going from the
        // messages alone and skip the `/runs` call.  `/runs` is only consulted
        // to CONFIRM completion on a tick where no active-run `in_progress`
        // row remains (which is ambiguous — could be "done" or just "nothing
        // written yet").
        //
        // IMPORTANT: `limit` and `after` are mutually exclusive on the backend
        // (400 if both supplied). Use `?after=<cursor>` alone when we have a
        // cursor, and `?limit=50` only for the very first fetch.
        const cursor = lastPolledMessageIdRef.current;
        const msgUrl = cursor
          ? `/api/sessions/${sessionId}/messages?after=${encodeURIComponent(cursor)}`
          : `/api/sessions/${sessionId}/messages?limit=50&complete_turns=true`;
        const msgRes = await fetchWithAuth(msgUrl, { headers: {} });
        if (!msgRes.ok) throw new Error(`HTTP ${msgRes.status}`);
        const msgData = await msgRes.json();
        if (!ownsActiveRun()) return;
        const rows = msgData.messages || [];

        // Active-run in-progress rows — used for both cursor safety and
        // completion detection below.
        const activeInProgressRows = rows.filter(
          (m: any) => m.run_id === activeRunId && m.status === "in_progress",
        );

        if (rows.length > 0) {
          // Advance the cursor to the newest stable row that comes BEFORE the
          // first active-run `in_progress` row.  In-progress rows are UPDATEd
          // in place by the backend (their created_at never changes), so if we
          // advanced the cursor to/past them the `?after=<id>` filter would
          // exclude them permanently and their final content would never load.
          //
          // The backend `after` cursor resolves the id to its created_at and
          // returns rows with `created_at > ref`, so advancing to a stable row
          // that SHARES the live row's timestamp would also exclude the live
          // row.  Restrict cursor-eligible rows to those STRICTLY OLDER (by
          // created_at) than the first active in_progress row; if we can't
          // parse that timestamp, don't advance this tick.
          const firstActiveIdx = rows.findIndex(
            (m: any) => m.run_id === activeRunId && m.status === "in_progress",
          );
          const firstActiveMs =
            firstActiveIdx === -1
              ? null
              : Date.parse(String(rows[firstActiveIdx].created_at));

          let cursorEligible: any[];
          if (firstActiveIdx === -1) {
            cursorEligible = rows;
          } else if (firstActiveMs !== null && Number.isFinite(firstActiveMs)) {
            cursorEligible = rows.slice(0, firstActiveIdx).filter((m: any) => {
              const ms = Date.parse(String(m.created_at));
              return Number.isFinite(ms) && ms < firstActiveMs;
            });
          } else {
            cursorEligible = [];
          }

          const newestStable = [...cursorEligible]
            .reverse()
            .find((m: any) => m.status !== "in_progress");
          if (newestStable && ownsActiveRun()) {
            lastPolledMessageIdRef.current = newestStable.id;
          }

          // Merge ALL returned rows. mergeMessages upserts by id (and now
          // returns the same reference on a content no-op, so identical polls
          // don't trigger a re-render). Including tool rows + full metadata
          // lets buildRenderItems pair tool start/complete rows during polling.
          //
          // dropOptimistic: this generation owns recovery, so any optimistic
          // (no-timestamp) messages still in the list are STALE placeholders
          // left over from an SSE stream that broke mid-run. A new live SSE
          // invalidates the generation before this callback can run. Their
          // client-side ids differ from the canonical DB rows we're merging,
          // so without dropping them the canonical rows sort chronologically
          // ABOVE the tail-pinned optimistic user message — the user sees the
          // latest reply above a duplicate of their own message. Drop them
          // once we actually have canonical replacements (rows.length > 0
          // here). Mirrors the resume() fallback and the
          // visibility/notification refetch paths.
          const incoming = rows.map(mapSessionMessage);
          if (historicalWindowRefRef.current?.current) {
            onHistoricalTailMessagesRef.current?.(incoming);
          } else {
            await commitRecoveryMessageMerge(() => {
              setMessages((prev) => {
                if (!ownsRecovery(generation)) return prev;
                const hasOptimistic = prev.some((m) => !isCanonical(m));
                return mergeMessages(
                  prev,
                  incoming,
                  hasOptimistic ? { dropOptimistic: true } : undefined,
                );
              });
            });
          }
        }

        // ── Completion detection ──────────────────────────────────────────
        // Assistant in-progress rows are always finalized by run
        // completion/error, so their presence reliably means the run is still
        // live — skip `/runs`.  Tool in-progress rows, however, can be
        // ORPHANED when a run fails or is cancelled before a matching
        // complete/error row is written (the backend doesn't close them), so
        // an orphan tool row would otherwise pin polling in "streaming"
        // forever.  Only short-circuit when EVERY active in-progress row is an
        // assistant row; otherwise fall through to confirm with `/runs`.
        if (
          activeInProgressRows.length > 0 &&
          activeInProgressRows.every((m: any) => m.role === "assistant")
        ) {
          return; // definitely still streaming — schedule next in finally
        }

        // No active `in_progress` row: AMBIGUOUS. Could be done, or just a
        // tick with nothing newly written. Confirm with `/runs`.
        const runRes = await fetchWithAuth(
          `/api/sessions/${sessionId}/runs`,
          { headers: {} },
        );
        if (!runRes.ok) throw new Error(`HTTP ${runRes.status}`);
        const runData: unknown = await runRes.json();
        if (!ownsActiveRun()) return;
        updateRecoverableFailedRun(runData);
        const currentRun = runsFromResponse(runData).find(
          (run) => run.run_id === activeRunId,
        );
        if (currentRun && currentRun.status === "running") {
          // Still running, just nothing written this tick — keep polling.
          return; // schedule next in finally
        }

        // ── Confirmed terminal/missing run → final drain ──────────────────
        if (!ownsActiveRun()) return;
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
        activeRunIdRef.current = null;

        // The final assistant row may have been committed at the same moment
        // the run flipped to done, so the fetch above may have missed it. Do
        // one more drain fetch from the current cursor before going ready.
        try {
          const finalUrl = lastPolledMessageIdRef.current
            ? `/api/sessions/${sessionId}/messages?after=${encodeURIComponent(lastPolledMessageIdRef.current)}`
            : `/api/sessions/${sessionId}/messages?limit=50&complete_turns=true`;
          const finalMsgRes = await fetchWithAuth(finalUrl, { headers: {} });
          if (finalMsgRes.ok) {
            const finalMsgData = await finalMsgRes.json();
            if (!ownsRecovery(generation)) return;
            const allFinal: AgnoChatMessage[] = (finalMsgData.messages || []).map(
              mapSessionMessage,
            );
            if (allFinal.length > 0) {
              // Same dropOptimistic rationale as the main poll merge above:
              // swap out any stale broken-SSE placeholders for the canonical
              // final rows so the just-finished turn isn't duplicated below it.
              if (historicalWindowRefRef.current?.current) {
                onHistoricalTailMessagesRef.current?.(allFinal);
              } else {
                await commitRecoveryMessageMerge(() => {
                  setMessages((prev) => {
                    if (!ownsRecovery(generation)) return prev;
                    const hasOptimistic = prev.some((m) => !isCanonical(m));
                    return mergeMessages(
                      prev,
                      allFinal,
                      hasOptimistic ? { dropOptimistic: true } : undefined,
                    );
                  });
                });
              }
            }
          }
        } catch (e) {
          // Non-fatal — best-effort
        }

        setRecoveryPolling(generation, false);
        // Only reset status if we're still in streaming (not already
        // submitted a new message in parallel)
        if (statusRef.current === "streaming") {
          setRecoveryStatus(generation, "ready");
        }
        return;
      } catch (err) {
        if (ownsRecovery(generation)) {
          console.error("[useAgnoChat] Poll error:", err);
        }
      } finally {
        pollInFlight = false;
        const pendingGeneration = pendingPollGeneration;
        pendingPollGeneration = null;
        if (
          pendingGeneration !== null &&
          ownsRecovery(pendingGeneration)
        ) {
          void pollMessages(pendingGeneration);
          return;
        }
        // Schedule the next poll 1 second after this one completes, regardless
        // of how long the request took. This prevents overlapping requests and
        // infinite loops when the endpoint is slow. Clear any existing timer
        // first so we never leave an orphaned tick running in parallel.
        if (
          ownsRecovery(generation) &&
          activeRunIdRef.current &&
          statusRef.current === "streaming"
        ) {
          if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
          }
          pollTimeoutRef.current = setTimeout(
            () => void pollMessages(generation),
            1000,
          );
        }
      }
    }

    return () => {
      cancelled = true;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
    // agentId is intentionally excluded: this effect only checks for
    // in-progress runs and polls messages — it never uses agentId.  When
    // refreshSessions() runs after a run completes, activeSession?.agent_id
    // can change identity (new API response), which would tear down and
    // recreate this entire polling loop for no reason, adding redundant
    // /runs and /messages fetches on every run completion.
  }, [sessionId, fetchWithAuth, updateRecoverableFailedRun, bootstrapRuns, bootstrapPending]);

  // ── Abort the live SSE reader on unmount (with first-event gate) ──
  //
  // The parent keys ChatInterface on sessionId, so switching sessions unmounts
  // this hook instance.  If a stream was in flight, its fetch/reader would
  // otherwise stay alive in the background — its setMessages calls target the
  // unmounted instance (silently dropped) while the connection leaks until the
  // server ends the stream.  Aborting on unmount closes that obsolete local
  // reader.
  //
  // The flow is split in two:
  //
  //   1. The SSE /chat/web stream controller (`abortRef`). We only abort THIS
  //      controller once we have positive evidence the server accepted the
  //      run — i.e. `hasReceivedFirstEventRef.current === true`. Before the
  //      first event arrives, the server POST may still be committing the
  //      message + run row; aborting the local fetch then would risk
  //      orphaning a run we never see in the stream. Leaving the read loop
  //      alive on the stale React instance is harmless (setMessages on an
  //      unmounted component is a React 18 no-op) and the mount-time /runs
  //      + /messages polling path on re-entry into any session recovers
  //      whatever the server persisted.
  //
  //   2. The upload / transcription controller (`uploadAbortControllerRef`).
  //      This is INTENTIONALLY not aborted on unmount — switching sessions
  //      mid-upload is the bug we're fixing. The multipart POST and the
  //      ElevenLabs /api/transcribe call both keep running; the optimistic
  //      user message that was inserted at send time displays the file chip
  //      in the bubble regardless. Only an explicit stop() takes this
  //      controller down.
  //
  // We deliberately do NOT POST /chat/web/stop on unmount: switching
  // sessions must NOT cancel the server-side run.  The backend keeps running
  // and persists to the DB, so returning to the session recovers the live
  // output via the mount-time polling path above.  Empty deps → this
  // cleanup runs only on the true unmount, never on a re-render.
  useEffect(() => {
    return () => {
      if (hasReceivedFirstEventRef.current) {
        abortRef.current?.abort();
      }
      abortRef.current = null;
      // DO NOT abort uploadAbortControllerRef here. See note above.
    };
  }, []);

  // ── Send message (SSE streaming) ──
  const sendMessage = useCallback(
    async ({ text, files }: { text: string; files?: any[] }): Promise<SendMessageResult> => {
      // Defense-in-depth: never send an empty message (no text AND no files).
      // The UI layer (PromptInput) already guards against this, but any other
      // caller of sendMessage (e.g. a future keyboard-shortcut or automation
      // path) would otherwise bypass that check and create a useless run.
      if ((text ?? "").trim() === "" && (files?.length ?? 0) === 0) {
        console.warn("[useAgnoChat] sendMessage called with empty text and no files — ignoring");
        return { outcome: "cancelled" };
      }
      if (historicalWindowRefRef.current?.current) {
        await beforeSendFromHistoricalWindowRef.current?.();
      }

      // Stop any active polling before starting a new SSE stream. This also
      // invalidates an already in-flight recovery fetch, whose old canonical
      // response must never get to drop this just-added optimistic user row.
      cancelRecovery();
      // A user-submitted normal turn supersedes any prior recovered failure.
      // The failed-run action performs its own post-turn /runs refresh below.
      setRecoverableFailedRun(null);
      // Clear the recovery flag from any previous resume() so the catch block
      // below runs normally if this new stream errors.
      abortedForRecoveryRef.current = false;
      hasReceivedFirstEventRef.current = false;

      // Two distinct abort controllers: the upload path and the SSE stream
      // path. They are deliberately separate so that unmounting this hook
      // instance (e.g. when the user switches sessions mid-upload) does NOT
      // cascade an abort into the multipart upload or /api/transcribe fetch.
      //
      // `uploadAbortController` is referenced by stop() via
      // uploadAbortControllerRef.current and is NEVER aborted on unmount —
      // it's the bug fix for "voice note is gone if I switch sessions".
      //
      // `streamController` is the one staged into abortRef.current. The
      // unmount cleanup aborts it ONLY after hasReceivedFirstEventRef flips
      // to true (see the unmount useEffect above).
      const uploadAbortController = new AbortController();
      const streamController = new AbortController();
      uploadAbortControllerRef.current = uploadAbortController;
      abortRef.current = streamController;

      const audioFiles = files?.filter((f: any) => {
        const mediaType = f.mediaType ?? f.type ?? "";
        return typeof mediaType === "string" && mediaType.startsWith("audio/");
      }) ?? [];
      // Keep voice preparation visible outside this hook instance. The handle
      // is completed after the upload/transcription preflight, or immediately
      // when the user explicitly stops the send.
      const voiceNoteProgress = audioFiles.length > 0
        ? beginVoiceNoteProgress(sessionId)
        : null;
      if (voiceNoteProgress) voiceNoteProgressHandlesRef.current.add(voiceNoteProgress);
      const completeVoiceNoteProgress = () => {
        if (!voiceNoteProgress) return;
        voiceNoteProgress.complete();
        voiceNoteProgressHandlesRef.current.delete(voiceNoteProgress);
      };

      // ── Upload files to the workspace BEFORE sending the message ──</newText>
      // The web UI uploads files directly to the agno-server's multipart
      // endpoint via the chat-bridge proxy, then sends only the returned
      // file paths in the chat message — NOT base64 data URLs.
      //
      // This is critical for large files: embedding a 2 GB file as base64
      // in the JSON body would OOM the browser and chat-bridge. The
      // multipart upload streams the file without buffering it entirely
      // in memory.
      // Build the optimistic user message NOW (before the upload starts) so
      // the file chip + text appear in the conversation immediately, even if
      // the user navigates away mid-upload. Without this, a failed or
      // backgrounded upload would have no visible local representation while
      // the draft snapshot is being retained. The file parts carry blob URLs
      // (no `uploaded_path` yet) — if the upload succeeds we patch the paths
      // in below; if it fails the optimistic row is removed and the composer
      // restores the local files for retry. The downstream SSE POST body
      // still reads `uploadedPaths` from the upload step separately.
      let uploadedPaths: string[] = [];
      const userMessageId = generateId();
      const initialUserMessage: AgnoChatMessage = {
        id: userMessageId,
        role: "user",
        parts: [
          ...(files?.map((f: any, i: number) => ({
            type: "file" as const,
            filename: f.filename ?? f.name ?? `file-${i}`,
            mediaType: f.mediaType ?? f.type,
            url: f.url,
            // uploaded_path intentionally omitted here — filled in after the
            // upload resolves (see "Patch uploaded_path into the optimistic
            // message" below).
          })) ?? []),
          { type: "text" as const, text },
        ],
      };

      // Optimistic user message — no timestamp, so the store pins it to the
      // tail (after canonical history) until the DB row reconciles it.
      setMessages((prev) => mergeMessages(prev, [initialUserMessage]));
      let assistantIdForSend: string | null = null;
      const removeUnsentOptimisticMessages = () => {
        setMessages((prev) => prev.filter((message) => (
          message.id !== userMessageId
          && (assistantIdForSend === null || message.id !== assistantIdForSend)
        )));
      };
      // Keep the ref synchronous with the status state. Recovery polling and
      // visibility paths consult it before React has necessarily committed the
      // "submitted" render.
      statusRef.current = "submitted";
      setStatus("submitted");

      // Keep the shared progress marker alive through every pre-chat async
      // step. The finally is important for an aborted or failed upload and
      // unexpected preflight errors: none may reach the normal send path.
      let voiceTranscript: string | undefined;
      try {
      if (files && files.length > 0) {
        setIsUploading(true);
        try {
          uploadedPaths = await uploadFilesToServer(
            sessionId,
            agentId,
            files,
            fetchWithAuth,
            uploadAbortController.signal,
          );
          console.info(
            `[useAgnoChat] Uploaded ${uploadedPaths.length} file(s) →`,
            uploadedPaths.map((p) => p.split("/").pop()),
          );
        } catch (err) {
          console.error("[useAgnoChat] File upload failed:", err);
          setIsUploading(false);
          // If the upload was aborted (user clicked stop), don't show an
          // error — the stop() handler already reset status to "ready".
          if (err instanceof Error && err.name === "AbortError") {
            return { outcome: "cancelled" };
          }
          // An attachment upload is part of the send.  Do not submit a
          // message whose file payload never reached the workspace: the
          // composer will restore the complete attempted payload as a draft.
          setIsTranscribing(false);
          statusRef.current = "ready";
          setStatus("ready");
          removeUnsentOptimisticMessages();
          return { outcome: "failed", error: err };
        }
        setIsUploading(false);
      }

      // ── Transcribe voice-note audio files ──
      // If any of the uploaded files are audio (voice notes), transcribe
      // them via the /api/transcribe proxy (ElevenLabs Scribe v2) BEFORE
      // sending the chat message.  The transcript is injected into the
      // agent's message with a <voice_transcript> marker so the agent
      // knows it's reading a transcript rather than typed text — no
      // elevenlabs-transcription skill needed.
      //
      // Transcription is best-effort: if it fails, the audio file is
      // still uploaded as a regular attachment and the agent can fall
      // back to its skill if it has one.
      if (audioFiles.length > 0) {
          voiceNoteProgress?.setPhase("transcribing");
          console.info(`[useAgnoChat] Transcribing ${audioFiles.length} voice note(s)…`);
          setIsTranscribing(true);
          try {
            const transcripts: string[] = [];
            for (const f of audioFiles) {
              const filename = f.filename ?? f.name ?? "voice-note.webm";
              // Get the blob — prefer rawFile, fall back to fetch(url)
              let blob: Blob;
              if ((f as any).rawFile instanceof File) {
                blob = (f as any).rawFile as Blob;
              } else {
                const res = await fetch(f.url);
                blob = await res.blob();
              }
              const formData = new FormData();
              formData.append("files", blob, filename);

              // Pass the user's UI locale as a soft language hint.
              // Scribe v2 still auto-detects and handles mixed-language
              // audio — this just improves accuracy on short/ambiguous clips.
              const locale = getCurrentLocale();
              const transcribeUrl = `/api/transcribe?language_code=${encodeURIComponent(locale)}&session_id=${encodeURIComponent(sessionId)}`;

              const transcribeController = new AbortController();
              let didTranscriptionTimeout = false;
              const timeoutId = window.setTimeout(() => {
                didTranscriptionTimeout = true;
                transcribeController.abort();
              }, VOICE_TRANSCRIPTION_TIMEOUT_MS);
              const abortTranscription = () => transcribeController.abort();
              uploadAbortController.signal.addEventListener("abort", abortTranscription, { once: true });

              try {
                const response = await fetchWithAuth(transcribeUrl, {
                  method: "POST",
                  body: formData,
                  signal: transcribeController.signal,
                });

                if (response.ok) {
                  const data = await response.json();
                  if (data.text && data.text.trim()) {
                    transcripts.push(data.text.trim());
                    console.info(
                      `[useAgnoChat] Transcribed "${filename}": ${data.text.length} chars`,
                    );
                  }
                } else {
                  const errText = await response.text().catch(() => "");
                  console.warn(
                    `[useAgnoChat] Transcription failed for "${filename}": ${response.status} ${errText.substring(0, 200)}`,
                  );
                }
              } catch (err) {
                if (didTranscriptionTimeout) {
                  console.warn(
                    `[useAgnoChat] Transcription timed out for "${filename}" after ${VOICE_TRANSCRIPTION_TIMEOUT_MS}ms — proceeding with audio as attachment`,
                  );
                } else {
                  throw err;
                }
              } finally {
                window.clearTimeout(timeoutId);
                uploadAbortController.signal.removeEventListener("abort", abortTranscription);
              }
            }
            if (transcripts.length > 0) {
              voiceTranscript = transcripts.join("\n\n");
            }
          } catch (err) {
            console.warn("[useAgnoChat] Voice transcription failed — proceeding with audio as attachment:", err);
          }
      }

      // The optimistic user message was inserted BEFORE the upload started
      // (see the top of sendMessage). This block only patches in fields that
      // were unavailable at insert time:
      //   * uploaded_path on each file part (set ONLY if the upload
      //     succeeded; user keeps blob: URL preview otherwise, and the
      //     canonical DB row will replace this message with the
      //     upload-aware version on reconciliation).
      //   * the optional voiceTranscript metadata.
      // patch_optimistic_user_message is a no-op if the optimistic message
      // was already reconciled (e.g. fast upload + dropOptimistic by
      // mount-time polling path).
      const patchOptimisticUserMessage = () => {
        setMessages((prev) => {
          const idx = prev.findIndex((m) => m.id === userMessageId);
          if (idx === -1) return prev;
          const cur = prev[idx];
          const next = {
            ...cur,
            parts: cur.parts?.map((p, i) =>
              p.type === "file" && uploadedPaths[i]
                ? { ...p, uploaded_path: uploadedPaths[i] }
                : p
            ),
            ...(voiceTranscript
              ? { metadata: { ...(cur.metadata ?? {}), voiceTranscript } }
              : {}),
          };
          return [...prev.slice(0, idx), next, ...prev.slice(idx + 1)];
        });
      };

      // Patch the optimistic message with uploaded_path + voiceTranscript now
      // that both async phases have resolved. See comment on
      // patchOptimisticUserMessage. The closure captures voiceTranscript and
      // uploadedPaths, both of which are now final.
      patchOptimisticUserMessage();
      } finally {
        completeVoiceNoteProgress();
      }

      try {
        // Only send the new user message — the agno-server is stateful and
        // stores every message in Postgres per session_id. Sending the full
        // conversation history on every turn caused 413 Request Entity Too
        // Large as sessions grew. The backend only uses `message.text` from
        // the latest user message; all prior history is retrieved server-side.
        const body = {
          id: sessionId,
          messages: [
            {
              id: userMessageId,
              role: "user" as const,
              parts: [
                ...(files?.map((f: any, i: number) => ({
                  type: "file" as const,
                  filename: f.filename ?? f.name ?? `file-${i}`,
                  mediaType: f.mediaType ?? f.type,
                  url: f.url,
                  uploaded_path: uploadedPaths[i],
                })) ?? []),
                { type: "text" as const, text },
              ],
            },
          ],
          trigger: "submit-message" as const,
          body: { session_id: sessionId, agent_id: agentId },
          // The server validates this against the shared locale map before it
          // becomes model-visible <user_meta> language guidance.
          locale: getCurrentLocale(),
          // Pre-uploaded file paths (from the multipart upload endpoint).
          // chat-bridge reads these to inject <uploaded_files> into the run
          // message — the files are already on disk, no need to re-upload.
          uploaded_paths: uploadedPaths.length > 0 ? uploadedPaths : undefined,
          // Voice-note transcript (from the /api/transcribe endpoint).
          // chat-bridge reads this to inject <voice_transcript> into the
          // run message — the agent sees the transcribed text directly
          // without needing the elevenlabs-transcription skill.
          voice_transcript: voiceTranscript,
        };

        lastEventIdRef.current = null;

        const response = await fetchWithAuth("/chat/web", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal: streamController.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          const error = new Error(`Chat request failed (${response.status})`);
          console.error(
            "[useAgnoChat] POST failed:",
            response.status,
            errorText
          );
          setIsTranscribing(false);
          statusRef.current = "ready";
          setStatus("ready");
          removeUnsentOptimisticMessages();
          return { outcome: "failed", error };
        }

        statusRef.current = "streaming";
        setStatus("streaming");

        // Create assistant message placeholder
        const assistantId = generateId();
        assistantIdRef.current = assistantId;
        // activeAssistantId tracks the CURRENT live assistant
        // placeholder that subsequent text deltas should append to.
        // Tool boundaries (`<<TOOL_CALL|start|name>>`) split the streaming
        // assistant placeholder into multiple distinct rows so the
        // streaming tail mirrors the canonical /messages row shape
        // [user, assistant_pre, tool, assistant_post].  This makes
        // `mergeMessages(prev, incoming, { dropOptimistic: true })` after
        // stream end a geometry-neutral swap — both paths produce the
        // same RenderItem[].  See chat-bridge/ui/src/lib/stream-parser.ts
        // and the module-level docstring on buildRenderItems for the
        // full rationale.
        let activeAssistantId = assistantId;
        assistantIdForSend = assistantId;
        // Per-stream local sequencing only — never a persisted/API key.
        const inFlightTools: InFlightLiveTool[] = [];

        setMessages((prev) =>
          mergeMessages(prev, [
            {
              id: assistantId,
              role: "assistant",
              content: "",
              parts: [{ type: "text", text: "" }],
            },
          ]),
        );

        // Keep the empty-conversation transcription state visible until the
        // optimistic transcript and streaming placeholder are both ready.
        // Clearing it after the stream handoff prevents a blank frame between
        // the transcription overlay and the first rendered conversation row.
        setIsTranscribing(false);

        // Parse SSE stream (data: <json>\n per line)
        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let didComplete = false;
        const textParser = new StreamMarkerParser();

        // ── Coalesced streaming updates ───────────────────────────────────
        //
        // SSE text deltas can arrive faster than the screen can paint
        // (hundreds per second).  Applying each delta with its own
        // `setMessages` forced one React commit — and a full re-render of the
        // conversation (buildRenderItems + a Streamdown markdown re-parse of
        // the entire growing message) — per token.  That saturated the main
        // thread and made the whole UI, including the text input, lag.
        //
        // Instead we accumulate text fragments per target assistant row and
        // flush them in a single `setMessages` at most once per animation
        // frame.  Structural ops (tool boundaries) and non-delta replaces
        // flush the pending buffer first so ordering is preserved.
        //
        // (React 18+ already auto-batches state updates inside async
        // callbacks, so the earlier per-op comment claiming it does not was
        // inaccurate — but batching alone would not throttle the render rate;
        // frame coalescing does.)
        let rafHandle: ReturnType<typeof requestAnimationFrame> | null = null;
        // assistant row id → text pending append on the next flush.
        const pendingTextById = new Map<string, string>();

        const flushPendingText = () => {
          if (rafHandle != null) {
            cancelAnimationFrame(rafHandle);
            rafHandle = null;
          }
          if (pendingTextById.size === 0) return;
          const updates = [...pendingTextById];
          pendingTextById.clear();
          setMessages((prev) => {
            let next: AgnoChatMessage[] | null = null;
            for (const [id, frag] of updates) {
              const arr = next ?? prev;
              const idx = arr.findIndex((m) => m.id === id);
              if (idx === -1) continue;
              if (!next) next = [...prev];
              const cur = next[idx];
              const nextText = (cur.content ?? "") + frag;
              next[idx] = {
                ...cur,
                content: nextText,
                parts: [{ type: "text", text: nextText }],
              };
            }
            return next ?? prev;
          });
        };

        const scheduleFlush = () => {
          if (rafHandle == null) {
            rafHandle = requestAnimationFrame(flushPendingText);
          }
        };

        // ── Helper: apply ops produced by the parser to the message list ──
        const applyOps = (ops: StreamMarkerOp[], resetActiveBeforeApply: boolean) => {
          for (const op of ops) {
            if (op.type === "text") {
              const targetId = activeAssistantId;
              const fragment = op.text;
              if (resetActiveBeforeApply) {
                // Non-delta replace: drop any buffered text for this row and
                // overwrite the active placeholder's content.
                pendingTextById.delete(targetId);
                setMessages((prev) => {
                  const idx = prev.findIndex((m) => m.id === targetId);
                  if (idx === -1) return prev;
                  const nextText = fragment;
                  return [
                    ...prev.slice(0, idx),
                    {
                      ...prev[idx],
                      content: nextText,
                      parts: [{ type: "text", text: nextText }],
                    },
                    ...prev.slice(idx + 1),
                  ];
                });
                resetActiveBeforeApply = false; // only first text op resets
                continue;
              }
              // Buffer the fragment for this row; flush on the next frame.
              pendingTextById.set(
                targetId,
                (pendingTextById.get(targetId) ?? "") + fragment,
              );
              scheduleFlush();
            } else if (op.type === "hitl_paused") {
              // HITL: the agent run paused for human approval.
              // Flush any pending text, then insert an approval-pending
              // message into the chat. The UI renders an ApprovalCard
              // for this message. When the user approves/rejects,
              // continueRun() is called to resume the run.
              flushPendingText();
              const approvalId = generateId();
              setMessages((prev) => [
                ...prev,
                {
                  id: approvalId,
                  role: "assistant",
                  content: "",
                  parts: [{ type: "text", text: "" }],
                  metadata: {
                    hitlPaused: true,
                    runId: op.runId,
                    requirements: op.requirements,
                    agentId: agentId,
                    sessionId,
                  },
                },
              ]);
              // The run is paused — mark as ready so the UI doesn't
              // show a streaming spinner. The continue call will
              // start a new stream.
              pausedForHitlRef.current = true;
              setStatus("ready");
              didComplete = true;  // stop the SSE loop
            } else if (op.type === "compaction_marker") {
              // Context compaction: insert a system-role marker message
              // so buildRenderItems can render a dashed divider.  The
              // marker has empty content — the divider is purely visual.
              flushPendingText();
              const compactionId = generateId();
              setMessages((prev) => [
                ...prev,
                {
                  id: compactionId,
                  role: "system",
                  content: "",
                  parts: [{ type: "text", text: "" }],
                  metadata: {
                    phase: "compaction",
                    tokens_before: op.tokensBefore,
                    summary_length: op.summaryLength,
                  },
                },
              ]);
            } else if (op.type === "flash_mode") {
              setMessages((prev) => prev.map((message) =>
                message.id === activeAssistantId
                  ? { ...message, metadata: { ...message.metadata, flashMode: true } }
                  : message,
              ));
            } else {
              // tool_marker — structural change.  Flush buffered text first so
              // the freshly minted rows are appended after the current text is
              // committed and ordering is preserved.
              flushPendingText();
              const { status, toolName } = op;
              if (status === "start") {
                // 1. Mint a tool placeholder with status "in_progress".
                // 2. Mint a fresh assistant row that subsequent text
                //    will append to.  Keep `activeAssistantId` and
                //    `assistantIdRef.current` in sync so the error path
                //    marks the right row on interrupt.
                const toolId = generateId();
                const newAssistantId = generateId();
                inFlightTools.push({ id: toolId, name: toolName });
                activeAssistantId = newAssistantId;
                assistantIdRef.current = newAssistantId;
                setMessages((prev) =>
                  mergeMessages(prev, [
                    {
                      id: toolId,
                      role: "tool",
                      content: "",
                      parts: [{ type: "text", text: "" }],
                      metadata: {
                        status: "in_progress",
                        phase: "start",
                        toolName,
                        toolDetailSource: "live",
                        liveToolDetails: mergeLiveToolDetails(undefined, op.details),
                      },
                    },
                    {
                      id: newAssistantId,
                      role: "assistant",
                      content: "",
                      parts: [{ type: "text", text: "" }],
                    },
                  ]),
                );
              } else {
                // end | error
                notifyToolCompleted(op);
                // 1. Take the stream-local in-flight start for this tool.
                //    If the queue is empty (interrupted/lost), mint a
                //    synthetic row tagged with phase "complete" so the
                //    renderer can still show a resolved badge.
                // 2. Mint a fresh empty assistant placeholder.  This
                //    mirrors the canonical shape: every tool row has an
                //    assistant row both before AND after it.
                const toolId = takeInFlightLiveTool(inFlightTools, toolName);
                const newAssistantId = generateId();
                activeAssistantId = newAssistantId;
                assistantIdRef.current = newAssistantId;
                const updates: AgnoChatMessage[] = [];
                if (toolId) {
                  updates.push({
                    id: toolId,
                    role: "tool",
                    content: "",
                    parts: [{ type: "text", text: "" }],
                    metadata: {
                      status: status === "error" ? "error" : "complete",
                      phase: "complete",
                      toolName,
                      toolDetailSource: "live",
                    },
                  });
                } else {
                  // No matching start row (e.g. SSE ordering went
                  // wrong, or stream restarted mid-flight).  Mint a
                  // synthetic complete-or-error row so the badge still
                  // has a place to live in the message list.
                  const syntheticId = generateId();
                  updates.push({
                    id: syntheticId,
                    role: "tool",
                    content: "",
                    parts: [{ type: "text", text: "" }],
                    metadata: {
                      status: status === "error" ? "error" : "complete",
                      phase: "complete",
                      toolName,
                      toolDetailSource: "live",
                      liveToolDetails: mergeLiveToolDetails(undefined, op.details),
                    },
                  });
                }
                updates.push({
                  id: newAssistantId,
                  role: "assistant",
                  content: "",
                  parts: [{ type: "text", text: "" }],
                });
                setMessages((prev) => {
                  const resolved = updates.map((update) => {
                    if (update.role !== "tool") return update;
                    const prior = prev.find((message) => message.id === update.id);
                    return {
                      ...update,
                      metadata: {
                        ...update.metadata,
                        liveToolDetails: mergeLiveToolDetails(
                          prior?.metadata?.liveToolDetails,
                          op.details,
                        ),
                      },
                    };
                  });
                  return mergeMessages(prev, resolved);
                });
              }
            }
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // First-event gate: mark that the server has begun sending stream
          // events. From this point on, the unmount cleanup is allowed to
          // abort streamController safely — the server has accepted the run
          // and the run row + initial events are durably persisted in the
          // SSE protocol's lifetime. Before this flip, the server POST may
          // still be committing; aborting locally would risk orphaning a
          // run we never see in the stream.
          if (
            !hasReceivedFirstEventRef.current &&
            value !== undefined &&
            value.length > 0
          ) {
            hasReceivedFirstEventRef.current = true;
          }

          buffer += decoder.decode(value, { stream: true });

          let lineEnd: number;
          while ((lineEnd = buffer.indexOf("\n")) !== -1) {
            const line = buffer.slice(0, lineEnd).trim();
            buffer = buffer.slice(lineEnd + 1);

            if (!line.startsWith("data:")) continue;

            const dataStr = line.slice(5).trim();
            if (!dataStr || dataStr === "[DONE]") continue;

            try {
              const data = JSON.parse(dataStr);

              // Track event id for resume
              if (data.id) {
                lastEventIdRef.current = data.id;
              }

              // Terminal events: mark completion and skip text extraction
              if (data.type === "finish" || data.type === "done" || data.type === "completed" || data.type === "error") {
                didComplete = true;
                continue;
              }

              // Skip non-terminal events that carry no text content
              if (data.type === "start" || data.type === "start-step" || data.type === "finish-step" || data.type === "text-start" || data.type === "text-end") continue;

              // Extract text from various AI SDK chunk formats
              let chunkText = "";
              if (data.type === "text-delta" && data.delta) {
                chunkText = data.delta;
              } else if (data.parts) {
                chunkText = data.parts
                  .filter((p: any) => p.type === "text")
                  .map((p: any) => p.text)
                  .join("");
              } else if (data.text) {
                chunkText = data.text;
              } else if (data.content) {
                chunkText = data.content;
              }

              // Distinguish between a delta (append) and a replacement.
              // The AI SDK streaming path always sends deltas, but the
              // hook also accepts synthetic replacements for non-stream
              // callers — those should clear the active placeholder
              // before applying.
              const isDelta =
                data.type === "text-delta" ||
                data.delta === true ||
                data.isDelta === true;

              const ops = textParser.feed(chunkText);
              if (ops.length === 0) continue;
              applyOps(ops, !isDelta);
            } catch (e) {
              console.warn(
                "[useAgnoChat] Invalid SSE chunk:",
                dataStr
              );
            }
          }
        }

        // Drain the mark-parser at end-of-stream so any residual text
        // (and any marker prefix the server failed to close) lands in
        // the active assistant placeholder as plain text.  Without
        // this the last few tokens of a stream would silently vanish
        // from the visible UI.
        const residual = textParser.flush();
        if (residual.length > 0) {
          applyOps(residual, false);
        }

        // Commit any buffered tokens synchronously before we reconcile against
        // the DB so the final on-screen content matches the persisted message.
        flushPendingText();

        // After the loop, check if the stream ended without a terminal event
        if (!didComplete) {
          console.warn("[useAgnoChat] Stream ended without terminal event — likely interrupted");
          const error = new Error("Chat stream ended before completion");
          if (!hasReceivedFirstEventRef.current) {
            statusRef.current = "ready";
            setStatus("ready");
            removeUnsentOptimisticMessages();
            return { outcome: "failed", error };
          }
          statusRef.current = "error";
          setStatus("error");
          // Mark the assistant message as having a streaming error
          setMessages((prev) => {
            const idx = prev.findIndex((m) => m.id === assistantId);
            if (idx !== -1) {
              return [...prev.slice(0, idx), { ...prev[idx], streamingError: true }, ...prev.slice(idx + 1)];
            }
            return prev;
          });
          return { outcome: "sent", error };
        }

        statusRef.current = "ready";
        setStatus("ready");
        return { outcome: "sent" };
      } catch (err) {
        setIsTranscribing(false);
        if (err instanceof Error && err.name === "AbortError") {
          console.log("[useAgnoChat] Stream aborted by user");
          if (!abortedForRecoveryRef.current) {
            statusRef.current = "ready";
            setStatus("ready");
          }
          // When abortedForRecovery is true, resume() is handling the
          // status transition — don't clobber it.
          return hasReceivedFirstEventRef.current
            ? { outcome: "sent" }
            : { outcome: "cancelled", error: err };
        } else {
          console.error("[useAgnoChat] Stream error:", err);
          if (!hasReceivedFirstEventRef.current && !abortedForRecoveryRef.current) {
            statusRef.current = "ready";
            setStatus("ready");
            removeUnsentOptimisticMessages();
            return { outcome: "failed", error: err };
          }
          if (!abortedForRecoveryRef.current) {
            statusRef.current = "error";
            setStatus("error");
            // Mark the assistant message as having a streaming error
            setMessages((prev) => {
              const idx = prev.findIndex((m) => m.id === assistantIdRef.current);
              if (idx !== -1) {
                return [...prev.slice(0, idx), { ...prev[idx], streamingError: true }, ...prev.slice(idx + 1)];
              }
              return prev;
            });
          }
          // When abortedForRecovery is true, resume() owns the recovery —
          // skip the streamingError marker so the auto-resume effect
          // doesn't loop.
          return { outcome: "sent", error: err };
        }
      } finally {
        // Important: we DO NOT null abortRef.current here.
        //
        // The unmount cleanup checks `hasReceivedFirstEventRef.current`
        // and then calls `abortRef.current?.abort()`. If we cleared the
        // ref in this finally, an unmount that happens AFTER the SSE
        // stream has finished normally would still see a `true` first-event
        // gate but a null ref — and the abort would silently no-op. That
        // means a long-finished reader could leak setMessages into a
        // stale React instance for the brief window between sendMessage()
        // resolving and the unmount effect running.
        //
        // The controller is referenced (not owned) by abortRef; the ref
        // is overwritten on the next sendMessage() call so we never
        // accumulate them. The unmount cleanup nulls it after the gate
        // check.
        uploadAbortControllerRef.current = null;
      }
    },
    [sessionId, agentId, fetchWithAuth, cancelRecovery]
  );

  const refreshRecoverableFailedRun = useCallback(
    async (targetSessionId: string): Promise<RecoverableFailedRun | null> => {
      try {
        const response = await fetchWithAuth(
          `/api/sessions/${targetSessionId}/runs`,
          { headers: {} },
        );
        if (sessionIdRef.current !== targetSessionId) return null;
        if (!response.ok) {
          setRecoverableFailedRun(null);
          return null;
        }

        const data: unknown = await response.json();
        if (sessionIdRef.current !== targetSessionId) return null;
        return updateRecoverableFailedRun(data);
      } catch (error) {
        if (sessionIdRef.current === targetSessionId) {
          setRecoverableFailedRun(null);
          console.warn("[useAgnoChat] Failed to refresh recoverable failed run:", error);
        }
        return null;
      }
    },
    [fetchWithAuth, updateRecoverableFailedRun],
  );

  // A recovered failed run is continued by adding a new, normal message to
  // the saved conversation. Do not use continueRun(): that endpoint remains
  // exclusively for paused HITL runs and their resolved tool payloads.
  const continueFromFailedRun = useCallback(async () => {
    const failedRun = recoverableFailedRun;
    const capturedSessionId = sessionId;
    if (!failedRun) return;

    // Re-read exactly once before sending. A newer terminal or in-progress
    // turn invalidates this button; a newer failed turn replaces its signal.
    const latestFailedRun = await refreshRecoverableFailedRun(capturedSessionId);
    if (sessionIdRef.current !== capturedSessionId) return;
    if (!latestFailedRun || latestFailedRun.runId !== failedRun.runId) return;

    // Hide the old affordance while the new turn runs. If no new run is
    // accepted, the post-send refresh below restores the still-current signal.
    setRecoverableFailedRun(null);
    await sendMessage({ text: FAILED_RUN_CONTINUATION_PROMPT });

    // sendMessage resolves after its SSE turn settles. Re-derive the signal
    // so a normal completion clears it and a new recovered failure updates it.
    if (sessionIdRef.current === capturedSessionId) {
      await refreshRecoverableFailedRun(capturedSessionId);
    }
  }, [recoverableFailedRun, refreshRecoverableFailedRun, sendMessage, sessionId]);

  // ── Resume a previously-interrupted stream via polling ──
  //
  // Delegates to the same checkAndPoll path used when entering a session that
  // has an in-progress run, so there is a single recovery code path regardless
  // of how the interruption occurred.
  const resume = useCallback(async () => {
    // Stamp the message so the auto-resume effect doesn't loop.
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.role === "assistant" && m.streamingError);
      if (idx !== -1) {
        return [...prev.slice(0, idx), { ...prev[idx], streamingError: false, resumeTimestamp: Date.now() }, ...prev.slice(idx + 1)];
      }
      return prev;
    });

    // ── Abort any stuck SSE reader ──────────────────────────────────────
    // When the tab was hidden and the SSE stream broke silently, the reader
    // may still be pending (the browser keeps the fetch alive but no data
    // arrives).  Aborting it unblocks the sendMessage() catch block, which
    // would otherwise race with our recovery logic here.  The
    // abortedForRecoveryRef flag tells the catch block to stand down —
    // resume() owns the status transition and the message fetch.
    if (abortRef.current) {
      abortedForRecoveryRef.current = true;
      abortRef.current.abort();
      abortRef.current = null;
    }

    // Optimistically show the streaming indicator while we look up the run.
    statusRef.current = "streaming";
    setStatus("streaming");

    // Kick off polling. checkAndPollRef.current is kept in sync with the
    // current session's checkAndPoll closure by the mount useEffect.
    await checkAndPollRef.current();

    // If checkAndPoll found no active run (it already finished while we were
    // disconnected), activeRunIdRef is still null. Fetch the latest messages
    // once to ensure we didn't miss the final assistant turn, then go ready.
    if (!activeRunIdRef.current && statusRef.current === "streaming") {
      try {
        // Fetch a generous page of recent messages. We use ?limit=50 (not a
        // cursor-based ?after fetch) because the optimistic SSE placeholders
        // from the broken stream have random client IDs with no createdAt —
        // selectNewestCanonicalId would return a stale cursor that misses
        // the canonical rows we need. A limit fetch returns the latest N
        // DB rows regardless of cursor state.
        //
        // We use mapSessionMessage (the complete mapping with status, runId,
        // phase, toolName metadata) instead of the previous inline mapping
        // that dropped these fields. Without the full metadata, tool rows
        // fetched from the DB don't carry their status ("complete"), so
        // buildRenderItems pairs them with the stale optimistic "in_progress"
        // tool rows and the UI shows a pending tool call forever.
        //
        // dropOptimistic: true swaps out the stale optimistic SSE
        // placeholders (user message, assistant stubs, in-progress tool
        // rows) for the canonical DB rows. Without this, the optimistic
        // messages remain in the list alongside their canonical
        // counterparts (different IDs), and the user sees duplicate /
        // stale content.
        const msgRes = await fetchWithAuth(
          `/api/sessions/${sessionId}/messages?limit=50&complete_turns=true`,
          { headers: {} }
        );
        if (msgRes.ok) {
          const msgData = await msgRes.json();
          const rows = msgData.messages || [];
          if (rows.length > 0) {
            const incoming = rows.map(mapSessionMessage);
            if (historicalWindowRefRef.current?.current) {
              onHistoricalTailMessagesRef.current?.(incoming);
            } else {
              await commitRecoveryMessageMerge(() => {
                setMessages((prev) => {
                  // Only drop optimistic messages when we actually have
                  // canonical replacements to show. mergeMessages with
                  // dropOptimistic + empty incoming would wipe the optimistic
                  // tail and leave nothing.
                  const hasOptimistic = prev.some((m) => !isCanonical(m));
                  return mergeMessages(
                    prev,
                    incoming,
                    hasOptimistic ? { dropOptimistic: true } : undefined,
                  );
                });
              });
            }
          }
        }
      } catch (e) {
        // Non-fatal — best-effort
        console.warn("[useAgnoChat] resume() fallback message fetch failed:", e);
      }
      setStatus("ready");
      statusRef.current = "ready";
    }
  }, [commitRecoveryMessageMerge, fetchWithAuth, sessionId, setMessages]);

  // Auto-resume interrupted streams without requiring a manual button click.
  // Uses polling of the messages API.
  // resumeTimestamp on the message prevents this from looping.
  useEffect(() => {
    // Find any assistant message flagged with a streaming error that hasn't
    // already been stamped with a resumeTimestamp.  Previously this only
    // checked the last message, which missed the error if a concurrent
    // merge/sort moved it off the tail.
    const erroredMsg = messages.find(
      (m) => m.role === "assistant" && m.streamingError && !m.resumeTimestamp
    );
    if (status === "error" && erroredMsg) {
      console.log("[useAgnoChat] Auto-resuming interrupted stream via polling…");
      resume();
    }
  }, [status, messages, resume]);

  // ── Stop ──
  const stop = useCallback(() => {
    cancelRecovery();
    setIsUploading(false);
    setIsTranscribing(false);
    statusRef.current = "ready";
    setStatus("ready");

    // A stopped upload/transcription is no longer pending, even if a mocked
    // or non-cooperative transport takes a moment to reject its abort signal.
    for (const progress of voiceNoteProgressHandlesRef.current) {
      progress.complete();
    }
    voiceNoteProgressHandlesRef.current.clear();

    // Capture the controllers now; we abort both local fetches only AFTER the
    // server-side cancel request resolves. Aborting first would close our
    // connection and let the server drop the run from its active-run registry
    // before the cancel request can find it, so the upstream agent run would
    // keep going. Telling the server to cancel persists partial output and
    // sets the run status to `cancelled`.
    //
    // stop() is the only place that aborts the upload controller. The unmount
    // cleanup intentionally does NOT touch it (we want to keep the multipart
    // upload + /api/transcribe running if the user navigates away mid-send),
    // so stop() must be explicit. stop() also ignores the first-event gate —
    // user cancellations are unconditional.
    const streamController = abortRef.current;
    const uploadController = uploadAbortControllerRef.current;
    void fetchWithAuth("/chat/web/stop", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    })
      .catch((e) => console.warn("[useAgnoChat] cancel request failed:", e))
      .finally(() => {
        streamController?.abort();
        uploadController?.abort();
      });
  }, [fetchWithAuth, sessionId, cancelRecovery]);

  // ── HITL: Continue a paused run ──
  // Called by the ApprovalCard when the user approves or rejects a tool.
  // POSTs to /api/agents/{id}/runs/{runId}/continue with the resolved
  // tool executions, then streams the resumed SSE response back into the
  // chat — appending new text to a fresh assistant message.
  const continueRun = useCallback(
    async (
      runId: string,
      tools: string,
      secretInputs?: { requirement_id: string; value: string },
      onAccepted?: () => void,
    ) => {
      if (!agentId) {
        console.warn("[useAgnoChat] continueRun: no agentId");
        throw new Error(i18n.t("approval.continueFailed"));
      }

      // Clear the HITL pause flag — the run is being resumed, so
      // post-stream reconciliation should behave normally again.
      pausedForHitlRef.current = false;

      setStatus("streaming" as ChatStatus);

      // Instead of removing the approval-pending message, mark it as
      // resolved so the ApprovalCard flips to its confirmation state
      // ("✓ Approved" / "✕ Rejected") and stays visible while the
      // resumed stream content flows into a new assistant message below.
      // We parse the `tools` payload to determine the confirmed outcome.
      let confirmed = true; // default: approved
      try {
        const parsed = JSON.parse(tools) as Array<{ confirmed?: boolean }>;
        if (parsed.length > 0 && parsed[0].confirmed === false) {
          confirmed = false;
        }
      } catch { /* default to approved */ }

      setMessages((prev) =>
        prev.map((m) => {
          if (!(m.metadata?.hitlPaused && m.metadata?.runId === runId)) return m;
          // Update the requirements so the ApprovalCard's resolved-state
          // useEffect can determine the outcome from confirmed field.
          const requirements = (m.metadata.requirements ?? []) as Array<{
            tool_execution?: { confirmed?: boolean };
          }>;
          const updatedRequirements = requirements.map((req) => ({
            ...req,
            tool_execution: {
              ...req.tool_execution,
              confirmed,
            },
          }));
          return {
            ...m,
            metadata: {
              ...m.metadata,
              hitlResolved: true,
              requirements: updatedRequirements,
            },
          };
        }),
      );

      // Add a fresh assistant placeholder for the resumed content
      const resumedAssistantId = generateId();
      setMessages((prev) => [
        ...prev,
        {
          id: resumedAssistantId,
          role: "assistant",
          content: "",
          parts: [{ type: "text", text: "" }],
        },
      ]);

      let requestSecretInputs = secretInputs;
      let decisionConflict = false;
      try {
        const hasSecretInput = requestSecretInputs !== undefined;
        const requestBody: Record<string, unknown> = {
          session_id: sessionId,
          tools,
        };
        if (hasSecretInput) {
          requestBody.secret_inputs = requestSecretInputs;
        }
        // Keep the browser-facing route stable; the authenticated proxy
        // selects the dedicated upstream endpoint when secret_inputs exists.
        const continuePath =
          `/api/agents/${encodeURIComponent(agentId)}/runs/${encodeURIComponent(runId)}/continue`;
        const response = await fetchWithAuth(
          continuePath,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(hasSecretInput ? { "Cache-Control": "no-store" } : {}),
            },
            body: JSON.stringify(requestBody),
          },
        );

        if (!response.ok || !response.body) {
          decisionConflict = response.status === 409;
          if (decisionConflict) {
            const body = await response.json().catch(() => null) as { error?: unknown; detail?: unknown } | null;
            const detail = body?.error ?? body?.detail;
            const key = detail === "Approval already submitted" ? "approval.alreadySubmitted"
              : detail === "Run is not paused" ? "approval.runNotPaused"
              : typeof detail === "string" && detail.startsWith("Approval already resolved")
                ? "approval.alreadyResolved" : "approval.stateChanged";
            throw new Error(i18n.t(key));
          }
          if (hasSecretInput) {
            // Do not read or surface a secret-branch response body. The
            // backend deliberately returns only a generic status anyway.
            if (response.body) {
              await response.body.cancel().catch(() => undefined);
            }
            throw new Error(i18n.t("approval.secretContinueFailed", { status: response.status }));
          }
          const text = await response.text().catch(() => i18n.t("common:status.unknownError"));
          throw new Error(i18n.t("approval.continueFailedStatus", {
            status: response.status,
            details: text,
          }));
        }
        onAccepted?.();

        // ── Stream the resumed SSE response ──────────────────────────────
        //
        // The /continue endpoint now returns the SAME AI SDK SSE text-delta
        // format as /chat/web — with <<TOOL_CALL|…>>, <<HITL_PAUSED|…>>,
        // and <<COMPACTION|…>> markers embedded in the text stream.  We
        // consume it with the exact same StreamMarkerParser + applyOps
        // logic as sendMessage, so tool calls, HITL pauses, and compaction
        // markers all render identically for continuations and new
        // requests.
        //
        // The applyOps closure below is a copy of the one in sendMessage
        // (kept in sync).  It handles:
        //   • text deltas → append to the active assistant row (coalesced)
        //   • tool_marker start/end/error → mint tool + assistant rows
        //   • hitl_paused → insert an approval-pending message
        //   • compaction_marker → insert a compaction divider message

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let sawTerminal = false;
        let streamError: string | null = null;
        const textParser = new StreamMarkerParser();

        // Coalesced streaming state (mirrors sendMessage)
        let rafHandle: ReturnType<typeof requestAnimationFrame> | null = null;
        const pendingTextById = new Map<string, string>();
        let activeAssistantId = resumedAssistantId;
        const inFlightTools: InFlightLiveTool[] = [];

        const flushPendingText = () => {
          if (rafHandle != null) {
            cancelAnimationFrame(rafHandle);
            rafHandle = null;
          }
          if (pendingTextById.size === 0) return;
          const updates = [...pendingTextById];
          pendingTextById.clear();
          setMessages((prev) => {
            let next: AgnoChatMessage[] | null = null;
            for (const [id, frag] of updates) {
              const arr = next ?? prev;
              const idx = arr.findIndex((m) => m.id === id);
              if (idx === -1) continue;
              if (!next) next = [...prev];
              const cur = next[idx];
              const nextText = (cur.content ?? "") + frag;
              next[idx] = {
                ...cur,
                content: nextText,
                parts: [{ type: "text", text: nextText }],
              };
            }
            return next ?? prev;
          });
        };

        const scheduleFlush = () => {
          if (rafHandle == null) {
            rafHandle = requestAnimationFrame(flushPendingText);
          }
        };

        // Shared applyOps — identical to the one in sendMessage.
        // Handles text, tool_marker, hitl_paused, and compaction_marker ops.
        const applyOps = (ops: StreamMarkerOp[], resetActiveBeforeApply: boolean) => {
          for (const op of ops) {
            if (op.type === "text") {
              const targetId = activeAssistantId;
              const fragment = op.text;
              if (resetActiveBeforeApply) {
                pendingTextById.delete(targetId);
                setMessages((prev) => {
                  const idx = prev.findIndex((m) => m.id === targetId);
                  if (idx === -1) return prev;
                  const nextText = fragment;
                  return [
                    ...prev.slice(0, idx),
                    {
                      ...prev[idx],
                      content: nextText,
                      parts: [{ type: "text", text: nextText }],
                    },
                    ...prev.slice(idx + 1),
                  ];
                });
                resetActiveBeforeApply = false;
                continue;
              }
              pendingTextById.set(
                targetId,
                (pendingTextById.get(targetId) ?? "") + fragment,
              );
              scheduleFlush();
            } else if (op.type === "hitl_paused") {
              flushPendingText();
              const approvalId = generateId();
              setMessages((prev) => [
                ...prev,
                {
                  id: approvalId,
                  role: "assistant",
                  content: "",
                  parts: [{ type: "text", text: "" }],
                  metadata: {
                    hitlPaused: true,
                    runId: op.runId,
                    requirements: op.requirements,
                    agentId: agentId,
                    sessionId,
                  },
                },
              ]);
              pausedForHitlRef.current = true;
              setStatus("ready");
            } else if (op.type === "compaction_marker") {
              flushPendingText();
              const compactionId = generateId();
              setMessages((prev) => [
                ...prev,
                {
                  id: compactionId,
                  role: "system",
                  content: "",
                  parts: [{ type: "text", text: "" }],
                  metadata: {
                    phase: "compaction",
                    tokens_before: op.tokensBefore,
                    summary_length: op.summaryLength,
                  },
                },
              ]);
            } else if (op.type === "flash_mode") {
              setMessages((prev) => prev.map((message) =>
                message.id === activeAssistantId
                  ? { ...message, metadata: { ...message.metadata, flashMode: true } }
                  : message,
              ));
            } else {
              // tool_marker — structural change
              flushPendingText();
              const { status, toolName } = op;
              if (status === "start") {
                const toolId = generateId();
                const newAssistantId = generateId();
                inFlightTools.push({ id: toolId, name: toolName });
                activeAssistantId = newAssistantId;
                setMessages((prev) =>
                  mergeMessages(prev, [
                    {
                      id: toolId,
                      role: "tool",
                      content: "",
                      parts: [{ type: "text", text: "" }],
                      metadata: {
                        status: "in_progress",
                        phase: "start",
                        toolName,
                        toolDetailSource: "live",
                        liveToolDetails: mergeLiveToolDetails(undefined, op.details),
                      },
                    },
                    {
                      id: newAssistantId,
                      role: "assistant",
                      content: "",
                      parts: [{ type: "text", text: "" }],
                    },
                  ]),
                );
              } else {
                // end | error
                notifyToolCompleted(op);
                const toolId = takeInFlightLiveTool(inFlightTools, toolName);
                const newAssistantId = generateId();
                activeAssistantId = newAssistantId;
                const updates: AgnoChatMessage[] = [];
                if (toolId) {
                  updates.push({
                    id: toolId,
                    role: "tool",
                    content: "",
                    parts: [{ type: "text", text: "" }],
                    metadata: {
                      status: status === "error" ? "error" : "complete",
                      phase: "complete",
                      toolName,
                      toolDetailSource: "live",
                    },
                  });
                } else {
                  const syntheticId = generateId();
                  updates.push({
                    id: syntheticId,
                    role: "tool",
                    content: "",
                    parts: [{ type: "text", text: "" }],
                    metadata: {
                      status: status === "error" ? "error" : "complete",
                      phase: "complete",
                      toolName,
                      toolDetailSource: "live",
                      liveToolDetails: mergeLiveToolDetails(undefined, op.details),
                    },
                  });
                }
                updates.push({
                  id: newAssistantId,
                  role: "assistant",
                  content: "",
                  parts: [{ type: "text", text: "" }],
                });
                setMessages((prev) => {
                  const resolved = updates.map((update) => {
                    if (update.role !== "tool") return update;
                    const prior = prev.find((message) => message.id === update.id);
                    return {
                      ...update,
                      metadata: {
                        ...update.metadata,
                        liveToolDetails: mergeLiveToolDetails(
                          prior?.metadata?.liveToolDetails,
                          op.details,
                        ),
                      },
                    };
                  });
                  return mergeMessages(prev, resolved);
                });
              }
            }
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // First-event gate: mark that the server has begun sending stream
          // events. From this point on, the unmount cleanup is allowed to
          // abort streamController safely — the server has accepted the run
          // and the run row + initial events are durably persisted in the
          // SSE protocol's lifetime. Before this flip, the server POST may
          // still be committing; aborting locally would risk orphaning a
          // run we never see in the stream.
          if (
            !hasReceivedFirstEventRef.current &&
            value !== undefined &&
            value.length > 0
          ) {
            hasReceivedFirstEventRef.current = true;
          }

          buffer += decoder.decode(value, { stream: true });
          let lineEnd: number;
          while ((lineEnd = buffer.indexOf("\n")) !== -1) {
            const line = buffer.slice(0, lineEnd).trim();
            buffer = buffer.slice(lineEnd + 1);

            if (!line.startsWith("data:")) continue;
            const dataStr = line.slice(5).trim();
            if (!dataStr || dataStr === "[DONE]") continue;

            try {
              const data = JSON.parse(dataStr);

              // Terminal events
              if (data.type === "error") {
                streamError = String(data.errorText || data.message || i18n.t("approval.continueFailed"));
                break;
              }
              if (data.type === "finish" || data.type === "done" || data.type === "completed") {
                sawTerminal = true;
                break;
              }

              // Skip non-terminal events with no text content
              if (data.type === "start" || data.type === "start-step" || data.type === "finish-step" || data.type === "text-start" || data.type === "text-end") continue;

              // Extract text from various AI SDK chunk formats
              let chunkText = "";
              if (data.type === "text-delta" && data.delta) {
                chunkText = data.delta;
              } else if (data.parts) {
                chunkText = data.parts
                  .filter((p: any) => p.type === "text")
                  .map((p: any) => p.text)
                  .join("");
              } else if (data.text) {
                chunkText = data.text;
              } else if (data.content) {
                chunkText = data.content;
              }
              if (chunkText.includes("❌ Error:")) {
                streamError = chunkText.split("❌ Error:").pop()?.trim() || i18n.t("approval.continueFailed");
              }

              const isDelta =
                data.type === "text-delta" ||
                data.delta === true ||
                data.isDelta === true;

              const ops = textParser.feed(chunkText);
              if (ops.length === 0) continue;
              applyOps(ops, !isDelta);
            } catch {
              // Ignore unparseable SSE lines
            }
          }
        }

        // Drain residual text from the parser
        const residual = textParser.flush();
        if (residual.length > 0) {
          applyOps(residual, false);
        }

        // Commit any buffered tokens
        flushPendingText();

        if (streamError) {
          const upstreamStatus = /^HTTP (\d{3}): (.*)$/s.exec(streamError);
          if (upstreamStatus) {
            const status = Number(upstreamStatus[1]);
            const detail = upstreamStatus[2];
            if (status === 409) {
              decisionConflict = true;
              const key = detail === "Approval already submitted" ? "approval.alreadySubmitted"
                : detail === "Run is not paused" ? "approval.runNotPaused"
                : detail.startsWith("Approval already resolved")
                  ? "approval.alreadyResolved" : "approval.stateChanged";
              throw new Error(i18n.t(key));
            }
            throw new Error(hasSecretInput
              ? i18n.t("approval.secretContinueFailed", { status })
              : i18n.t("approval.continueFailedStatus", { status, details: detail }));
          }
          throw new Error(streamError);
        }
        if (!sawTerminal) throw new Error(i18n.t("approval.streamInterrupted"));

        setStatus("ready");
      } catch (err) {
        if (requestSecretInputs) {
          console.error("[useAgnoChat] secret continuation failed");
        } else {
          console.error("[useAgnoChat] continueRun error:", err);
        }
        setStatus("error");
        // Reconcile before exposing retry: Agno's detached producer may have
        // accepted the decision and kept running after the browser stream died.
        let canRetry = false;
        try {
          const [runResponse, messageResponse] = await Promise.all([
            fetchWithAuth(`/api/sessions/${sessionId}/runs`, { headers: {} }),
            fetchWithAuth(`/api/sessions/${sessionId}/messages?limit=50&complete_turns=true`, { headers: {} }),
          ]);
          if (runResponse.ok && messageResponse.ok) {
            const runs = runsFromResponse(await runResponse.json());
            const messages = await messageResponse.json();
            const run = runs.find((entry) => entry.run_id === runId);
            const marker = (messages.messages ?? []).find((entry: any) =>
              entry.run_id === runId && entry.metadata?.phase === "hitl_paused");
            canRetry = !decisionConflict && run?.status === "paused" && marker?.metadata?.resolved !== true;
          }
        } catch {
          // Unknown persistence state is not permission to submit again.
        }
        // Also remove the assistant placeholder that was added for the
        // resumed content (it's empty since the stream failed).
        setMessages((prev) =>
          prev
            .filter((m) => m.id !== resumedAssistantId) // drop empty placeholder
            .map((m) => {
              if (!(m.metadata?.hitlPaused && m.metadata?.runId === runId)) return m;
              const requirements = (m.metadata.requirements ?? []) as Array<{
                tool_execution?: { confirmed?: boolean };
              }>;
              const revertedRequirements = requirements.map((req) => ({
                ...req,
                tool_execution: {
                  ...req.tool_execution,
                  confirmed: undefined,
                },
              }));
              return canRetry ? {
                ...m,
                metadata: {
                  ...m.metadata,
                  hitlResolved: false,
                  requirements: revertedRequirements,
                },
              } : m;
            }),
        );
        pausedForHitlRef.current = canRetry;
        const failure = err instanceof Error ? err : new Error(i18n.t("approval.continueFailed"));
        Object.assign(failure, { retryable: canRetry });
        throw failure;
      } finally {
        // Drop the local reference immediately after the fetch/stream settles.
        requestSecretInputs = undefined;
      }
    },
    [agentId, sessionId, fetchWithAuth],
  );

  return {
    messages,
    status,
    isPolling,
    sendMessage,
    recoverableFailedRun,
    continueFromFailedRun,
    stop,
    setMessages,
    resume,
    isUploading,
    isTranscribing,
    continueRun,
    pausedForHitl: pausedForHitlRef,
  };
}
